import {
  Container,
  Paper,
  Title,
  Text,
  Group,
  Button,
  Badge,
  Stack,
  Table,
  ActionIcon,
  Tabs,
  Alert,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconEdit,
  IconSend,
  IconUsers,
  IconFileText,
  IconAward,
  IconChartBar,
  IconCheck,
  IconX,
  IconShoppingCart,
} from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import { useRFQ, useSendRFQ, useAwardRFQ, useSubmitRFQForApproval, useApproveRFQ, useRejectRFQ, useGeneratePO } from "../hooks/useRFQ";
import { useAuth } from "../../../auth/AuthContext";
import dayjs from "dayjs";
import { useState } from "react";
import { Modal, Textarea, MultiSelect, Select } from "@mantine/core";
import { rfqApi } from "../../../api/rfq";
import { useQuery } from "@tanstack/react-query";
import { adminVendorsApi } from "../../../api/adminVendors";

const statusColors: Record<string, string> = {
  DRAFT: "gray",
  PENDING_APPROVAL: "yellow",
  APPROVED: "green",
  SENT: "blue",
  QUOTES_RECEIVED: "cyan",
  UNDER_REVIEW: "yellow",
  AWARDED: "green",
  CANCELLED: "red",
  EXPIRED: "orange",
};

export function RFQDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { data: rfq, isLoading } = useRFQ(id);
  const sendRFQ = useSendRFQ();
  const awardRFQ = useAwardRFQ();
  const submitForApproval = useSubmitRFQForApproval();
  const approveRFQ = useApproveRFQ();
  const rejectRFQ = useRejectRFQ();
  const generatePO = useGeneratePO();

  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [awardModalOpen, setAwardModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState<string[]>([]);
  const [selectedQuotation, setSelectedQuotation] = useState<string>("");
  const [awardNotes, setAwardNotes] = useState("");
  const [approvalComments, setApprovalComments] = useState("");

  const { data: vendors } = useQuery({
    queryKey: ["admin-vendors"],
    queryFn: () => adminVendorsApi.listVendors({ status: "ACTIVE" }),
  });

  const { data: comparison } = useQuery({
    queryKey: ["rfq-comparison", id],
    queryFn: () => rfqApi.getComparison(id!),
    enabled: !!id && rfq?.status !== "DRAFT",
  });

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading RFQ...</Text>
      </Container>
    );
  }

  if (!rfq) {
    return (
      <Container size="xl" py="xl">
        <Alert color="red">RFQ not found</Alert>
      </Container>
    );
  }

  const canEdit =
    rfq.status === "DRAFT" &&
    (rfq.createdBy.id === user?.id ||
      user?.role === "ADMIN" ||
      user?.role === "PROCUREMENT_OFFICER" ||
      user?.role === "HEAD_OF_PROCUREMENT");
  const canSubmit = rfq.status === "DRAFT" && rfq.createdBy.id === user?.id;
  const canApprove =
    (user?.role === "HEAD_OF_PROCUREMENT" || user?.role === "ADMIN") &&
    rfq.status === "PENDING_APPROVAL";
  const canSend =
    (rfq.status === "DRAFT" || rfq.status === "APPROVED") &&
    (rfq.createdBy.id === user?.id ||
      user?.role === "ADMIN" ||
      user?.role === "PROCUREMENT_OFFICER" ||
      user?.role === "HEAD_OF_PROCUREMENT");
  const canAward =
    rfq.status === "QUOTES_RECEIVED" || rfq.status === "UNDER_REVIEW";
  const canViewComparison = rfq.quotations && rfq.quotations.length > 0;
  const canGeneratePO =
    rfq.status === "AWARDED" &&
    rfq.awardedQuotationId &&
    !rfq.purchaseOrderId &&
    (user?.role === "ADMIN" ||
      user?.role === "PROCUREMENT_OFFICER" ||
      user?.role === "HEAD_OF_PROCUREMENT");

  const handleSend = () => {
    // If no vendors selected, it will auto-select from categories
    sendRFQ.mutate(
      {
        id: rfq.id,
        data: {
          vendorIds: selectedVendors.length > 0 ? selectedVendors : undefined,
          method: "EMAIL",
        },
      },
      {
        onSuccess: () => {
          setSendModalOpen(false);
          setSelectedVendors([]);
        },
      }
    );
  };

  const handleAward = () => {
    if (!selectedQuotation || !awardNotes.trim()) {
      return;
    }
    awardRFQ.mutate(
      {
        id: rfq.id,
        data: {
          quotationId: selectedQuotation,
          notes: awardNotes,
        },
      },
      {
        onSuccess: () => {
          setAwardModalOpen(false);
          setSelectedQuotation("");
          setAwardNotes("");
        },
      }
    );
  };

  const vendorOptions =
    vendors?.map((v: any) => ({
      value: v.id,
      label: v.businessName || v.email,
    })) || [];

  return (
    <Container size="xl" py="xl">
      <Group mb="xl">
        <ActionIcon variant="subtle" onClick={() => navigate("/rfq")}>
          <IconArrowLeft size={20} />
        </ActionIcon>
        <Title order={2}>{rfq.title}</Title>
      </Group>

      <Tabs defaultValue="details">
        <Tabs.List>
          <Tabs.Tab value="details" leftSection={<IconFileText size={16} />}>
            Details
          </Tabs.Tab>
          <Tabs.Tab
            value="vendors"
            leftSection={<IconUsers size={16} />}
            disabled={rfq.status === "DRAFT"}
          >
            Vendors ({rfq.vendors?.length || 0})
          </Tabs.Tab>
          <Tabs.Tab
            value="quotations"
            leftSection={<IconFileText size={16} />}
            disabled={!canViewComparison}
          >
            Quotations ({rfq.quotations?.length || 0})
          </Tabs.Tab>
          {canViewComparison && (
            <Tabs.Tab
              value="comparison"
              leftSection={<IconChartBar size={16} />}
            >
              Comparison
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="details" pt="md">
          <Stack gap="md">
            <Paper shadow="sm" p="md">
              <Group justify="space-between" mb="md">
                <Group>
                  <Text fw={500}>RFQ Number:</Text>
                  <Text>{rfq.rfqNumber}</Text>
                </Group>
                <Badge color={statusColors[rfq.status]} variant="light" size="lg">
                  {rfq.status.replace("_", " ")}
                </Badge>
              </Group>

              <Stack gap="sm">
                <Group>
                  <Text fw={500} w={150}>
                    Category:
                  </Text>
                  <Badge variant="light">{rfq.category}</Badge>
                </Group>
                <Group>
                  <Text fw={500} w={150}>
                    Description:
                  </Text>
                  <Text style={{ flex: 1 }}>{rfq.description}</Text>
                </Group>
                <Group>
                  <Text fw={500} w={150}>
                    Deadline:
                  </Text>
                  <Text>{dayjs(rfq.deadline).format("DD MMM YYYY HH:mm")}</Text>
                </Group>
                <Group>
                  <Text fw={500} w={150}>
                    Currency:
                  </Text>
                  <Text>{rfq.currency}</Text>
                </Group>
                <Group>
                  <Text fw={500} w={150}>
                    Created By:
                  </Text>
                  <Text>{rfq.createdBy.fullName}</Text>
                </Group>
                {rfq.vendorCategories && rfq.vendorCategories.length > 0 && (
                  <Group>
                    <Text fw={500} w={150}>
                      Vendor Categories:
                    </Text>
                    <Group gap="xs">
                      {rfq.vendorCategories.map((vc) => (
                        <Badge key={vc.id} variant="light">
                          {vc.category.name}
                        </Badge>
                      ))}
                    </Group>
                  </Group>
                )}
              </Stack>
            </Paper>

            <Paper shadow="sm" p="md">
              <Title order={4} mb="md">
                Items
              </Title>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Item Name</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th>Quantity</Table.Th>
                    <Table.Th>Unit</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rfq.items.map((item) => (
                    <Table.Tr key={item.id}>
                      <Table.Td>{item.itemName}</Table.Td>
                      <Table.Td>{item.description || "-"}</Table.Td>
                      <Table.Td>{item.quantity}</Table.Td>
                      <Table.Td>{item.unit}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>

            <Group justify="flex-end">
              {canEdit && (
                <Button
                  variant="light"
                  leftSection={<IconEdit size={16} />}
                  onClick={() => navigate(`/rfq/${rfq.id}/edit`)}
                >
                  Edit
                </Button>
              )}
              {canSubmit && (
                <Button
                  leftSection={<IconFileText size={16} />}
                  onClick={() => submitForApproval.mutate(rfq.id)}
                  loading={submitForApproval.isPending}
                >
                  Submit for Approval
                </Button>
              )}
              {canApprove && (
                <>
                  <Button
                    color="green"
                    leftSection={<IconCheck size={16} />}
                    onClick={() => setApproveModalOpen(true)}
                  >
                    Approve
                  </Button>
                  <Button
                    color="red"
                    leftSection={<IconX size={16} />}
                    onClick={() => setRejectModalOpen(true)}
                  >
                    Reject
                  </Button>
                </>
              )}
              {canSend && (
                <Button
                  leftSection={<IconSend size={16} />}
                  onClick={() => setSendModalOpen(true)}
                >
                  Send RFQ to Vendors
                </Button>
              )}
              {canAward && (
                <Button
                  color="green"
                  leftSection={<IconAward size={16} />}
                  onClick={() => setAwardModalOpen(true)}
                >
                  Award RFQ
                </Button>
              )}
              {canGeneratePO && (
                <Button
                  color="blue"
                  leftSection={<IconShoppingCart size={16} />}
                  onClick={() => {
                    if (rfq.awardedQuotationId) {
                      generatePO.mutate({
                        rfqId: rfq.id,
                        quotationId: rfq.awardedQuotationId,
                      });
                    }
                  }}
                  loading={generatePO.isPending}
                >
                  Generate Purchase Order
                </Button>
              )}
              {rfq.purchaseOrderId && (
                <Button
                  variant="light"
                  leftSection={<IconShoppingCart size={16} />}
                  onClick={() => navigate(`/purchase-orders/${rfq.purchaseOrderId}`)}
                >
                  View Purchase Order
                </Button>
              )}
              {canViewComparison && (
                <Button
                  variant="light"
                  leftSection={<IconChartBar size={16} />}
                  onClick={() => navigate(`/rfq/${rfq.id}/comparison`)}
                >
                  View Comparison
                </Button>
              )}
            </Group>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="vendors" pt="md">
          <Paper shadow="sm" p="md">
            <Title order={4} mb="md">
              Invited Vendors
            </Title>
            {rfq.vendors && rfq.vendors.length > 0 ? (
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Vendor</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Quotation Submitted</Table.Th>
                    <Table.Th>Invited At</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rfq.vendors.map((mapping) => (
                    <Table.Tr key={mapping.id}>
                      <Table.Td>
                        {mapping.vendor.businessName || mapping.vendor.email}
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light">{mapping.status}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={mapping.quotationSubmitted ? "green" : "gray"}
                          variant="light"
                        >
                          {mapping.quotationSubmitted ? "Yes" : "No"}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {mapping.id ? dayjs(new Date()).format("DD MMM YYYY") : "N/A"}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : (
              <Text c="dimmed">No vendors invited yet</Text>
            )}
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="quotations" pt="md">
          <Paper shadow="sm" p="md">
            <Title order={4} mb="md">
              Received Quotations
            </Title>
            {rfq.quotations && rfq.quotations.length > 0 ? (
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Quotation Number</Table.Th>
                    <Table.Th>Vendor</Table.Th>
                    <Table.Th>Total Price</Table.Th>
                    <Table.Th>Delivery Days</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Submitted At</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rfq.quotations.map((quotation) => (
                    <Table.Tr key={quotation.id}>
                      <Table.Td>{quotation.quotationNumber}</Table.Td>
                      <Table.Td>
                        {quotation.vendor.businessName || quotation.vendor.email}
                      </Table.Td>
                      <Table.Td>
                        {quotation.currency} {Number(quotation.totalPrice).toFixed(2)}
                      </Table.Td>
                      <Table.Td>{quotation.deliveryDays} days</Table.Td>
                      <Table.Td>
                        <Badge
                          color={
                            quotation.status === "ACCEPTED"
                              ? "green"
                              : quotation.status === "SUBMITTED"
                              ? "blue"
                              : "gray"
                          }
                          variant="light"
                        >
                          {quotation.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {quotation.submittedAt
                          ? dayjs(quotation.submittedAt).format("DD MMM YYYY")
                          : "-"}
                      </Table.Td>
                      <Table.Td>
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() =>
                            navigate(`/rfq/${rfq.id}/quotations/${quotation.id}`)
                          }
                        >
                          View
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : (
              <Text c="dimmed">No quotations received yet</Text>
            )}
          </Paper>
        </Tabs.Panel>

        {canViewComparison && (
          <Tabs.Panel value="comparison" pt="md">
            <Paper shadow="sm" p="md">
              <Title order={4} mb="md">
                Quote Comparison
              </Title>
              {comparison ? (
                <Stack>
                  <Group>
                    <Text fw={500}>Total Quotations:</Text>
                    <Text>{comparison.summary.totalQuotations}</Text>
                  </Group>
                  {comparison.summary.l1Vendor && (
                    <Group>
                      <Text fw={500}>L1 Vendor:</Text>
                      <Text>{comparison.summary.l1Vendor}</Text>
                      <Text fw={500}>Price:</Text>
                      <Text>
                        {comparison.summary.l1Price
                          ? `${rfq.currency} ${Number(comparison.summary.l1Price).toFixed(2)}`
                          : "-"}
                      </Text>
                    </Group>
                  )}
                  <Button
                    onClick={() => navigate(`/rfq/${rfq.id}/comparison`)}
                  >
                    View Detailed Comparison
                  </Button>
                </Stack>
              ) : (
                <Text c="dimmed">Loading comparison...</Text>
              )}
            </Paper>
          </Tabs.Panel>
        )}
      </Tabs>

      <Modal
        opened={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        title="Send RFQ to Vendors"
        size="lg"
      >
        <Stack>
          <Text size="sm" c="dimmed">
            {rfq.vendorCategories && rfq.vendorCategories.length > 0
              ? "Leave vendors empty to auto-select from selected categories, or manually select specific vendors."
              : "Select vendors to send the RFQ to."}
          </Text>
          <MultiSelect
            label="Select Vendors (Optional)"
            placeholder="Choose specific vendors or leave empty to use categories"
            data={vendorOptions}
            value={selectedVendors}
            onChange={setSelectedVendors}
            searchable
            clearable
          />
          {rfq.vendorCategories && rfq.vendorCategories.length > 0 && (
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Selected Categories:
              </Text>
              <Group gap="xs">
                {rfq.vendorCategories.map((vc) => (
                  <Badge key={vc.id} variant="light">
                    {vc.category.name}
                  </Badge>
                ))}
              </Group>
            </Stack>
          )}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setSendModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              loading={sendRFQ.isPending}
            >
              Send RFQ
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={awardModalOpen}
        onClose={() => setAwardModalOpen(false)}
        title="Award RFQ"
        size="lg"
      >
        <Stack>
          <Select
            label="Select Quotation"
            placeholder="Choose quotation to award"
            data={
              rfq.quotations
                ?.filter((q) => q.status === "SUBMITTED")
                .map((q) => ({
                  value: q.id,
                  label: `${q.quotationNumber} - ${q.vendor.businessName || q.vendor.email} - ${q.currency} ${Number(q.totalPrice).toFixed(2)}`,
                })) || []
            }
            value={selectedQuotation}
            onChange={(value) => setSelectedQuotation(value || "")}
          />
          <Textarea
            label="Selection Notes"
            placeholder="Enter notes explaining the selection (required)"
            required
            minRows={3}
            value={awardNotes}
            onChange={(e) => setAwardNotes(e.target.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setAwardModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAward}
              loading={awardRFQ.isPending}
              disabled={!selectedQuotation || !awardNotes.trim()}
              color="green"
            >
              Award RFQ
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={approveModalOpen}
        onClose={() => setApproveModalOpen(false)}
        title="Approve RFQ"
      >
        <Stack>
          <Textarea
            label="Comments (Optional)"
            placeholder="Enter approval comments"
            value={approvalComments}
            onChange={(e) => setApprovalComments(e.target.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setApproveModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                approveRFQ.mutate(
                  { id: rfq.id, comments: approvalComments || undefined },
                  {
                    onSuccess: () => {
                      setApproveModalOpen(false);
                      setApprovalComments("");
                    },
                  }
                );
              }}
              loading={approveRFQ.isPending}
              color="green"
            >
              Approve
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        title="Reject RFQ"
      >
        <Stack>
          <Textarea
            label="Rejection Comments"
            placeholder="Enter rejection reason (required)"
            required
            value={approvalComments}
            onChange={(e) => setApprovalComments(e.target.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setRejectModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!approvalComments.trim()) {
                  return;
                }
                rejectRFQ.mutate(
                  { id: rfq.id, comments: approvalComments },
                  {
                    onSuccess: () => {
                      setRejectModalOpen(false);
                      setApprovalComments("");
                    },
                  }
                );
              }}
              loading={rejectRFQ.isPending}
              color="red"
              disabled={!approvalComments.trim()}
            >
              Reject
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

